import {
  Component,
  OnInit,
  signal,
  model,
  computed,
  output,
  ChangeDetectionStrategy,
  input,
  effect,
} from '@angular/core';
import { keyBy } from 'lodash';

import { ICountry } from '@shared/components/input-country-phone/models/country.models';
import { getAllCountries } from '@shared/components/input-country-phone/config/countries.config';
import { getExampleInternationalPhoneNumber } from '@shared/components/input-country-phone/config/example-phone-number.util';

import { FormsModule } from '@angular/forms';
import { CallComponentMethodPipe } from '@shared/pipes/call-component-method/call-component-method.pipe';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-kiosk-input-country-phone-atom',
  imports: [FormsModule, CallComponentMethodPipe, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './kiosk-input-country-phone-atom.component.html',
  styleUrls: ['./kiosk-input-country-phone-atom.component.scss'],
})
export class KioskInputCountryPhoneAtomComponent implements OnInit {
  readonly selectedIso = model<string>();
  readonly phone = model<string>('');
  readonly defaultRegionCode = input<string>('fr');
  readonly updatePhone = output<string>();

  /** Optional i18n key for local-format help. International format uses PHONE_INPUT.HELP_WITH_DIAL_CODE. */
  readonly helpTranslateKey = input<string>();

  // Add new input property to control local format and dropdown visibility
  readonly useLocalFormat = input<boolean>(true);

  protected readonly displayedPhone = computed(() => {
    const dialCode = this.selectedCountryDialCode();
    if (!dialCode) {
      return this.phone();
    }

    let phoneValue = this.phone();
    if (!phoneValue) {
      return '';
    }

    // If not using local format, use the international format
    if (phoneValue.startsWith(`+${dialCode}`)) {
      // Remove the dial code from the phone number and any non-digit characters
      const phoneWithoutDialCode = phoneValue.replace(`+${dialCode}`, '').replace(/\D/g, '');

      // Format the remaining digits with spaces
      let formattedNumber = '';

      // Add digits with spaces between them
      for (let i = 0; i < phoneWithoutDialCode.length; i++) {
        // For French numbers (common pattern: +33 6 00 22 66 77)
        if (dialCode === '33') {
          // First digit after country code
          if (i === 0) {
            formattedNumber += ' ' + phoneWithoutDialCode[i] + ' ';
          }
          // Add a space after every second digit after the first one
          else if (i > 1 && (i - 1) % 2 === 0) {
            formattedNumber += ' ' + phoneWithoutDialCode[i];
          } else {
            formattedNumber += phoneWithoutDialCode[i];
          }
        }
        // US numbers (common pattern: +1 234 567 8910)
        else if (dialCode === '1') {
          if (i === 0 || i === 3 || i === 6) {
            formattedNumber += ' ' + phoneWithoutDialCode[i];
          } else {
            formattedNumber += phoneWithoutDialCode[i];
          }
        }
        // Generic formatting for other countries (groups of 2-3 digits)
        else {
          if (i === 0) {
            formattedNumber += ' ' + phoneWithoutDialCode[i];
          } else if (i % 2 === 0) {
            formattedNumber += ' ' + phoneWithoutDialCode[i];
          } else {
            formattedNumber += phoneWithoutDialCode[i];
          }
        }
      }

      // Return the formatted number with country code
      const formattedNumberWithDialCode = `+${dialCode} ${formattedNumber}`;

      if (!this.useLocalFormat()) {
        return formattedNumberWithDialCode;
      }

      // If using local format, convert to local number format
      // Extract just the number part without the country code
      if (!formattedNumberWithDialCode || formattedNumberWithDialCode.startsWith(`+${dialCode}`)) {
        const phoneWithoutDialCode = formattedNumber.replace(/^ /, ''); // Replace the first space with '' for local format
        // Add leading 0 if user has started entering a number and it doesn't already start with 0
        const localNumber =
          phoneWithoutDialCode && !phoneWithoutDialCode.startsWith('0')
            ? `0${phoneWithoutDialCode}`
            : phoneWithoutDialCode;
        return localNumber;
      }
      return formattedNumberWithDialCode;
    }
    // If the phone does not start with the dial code, try to format it anyway
    const cleanPhone = phoneValue.replace(/\D/g, '');
    if (cleanPhone.length > 0) {
      let formattedNumber = '';
      for (let i = 0; i < cleanPhone.length; i++) {
        if (i % 2 === 0 && i > 0) {
          formattedNumber += ' ';
        }
        formattedNumber += cleanPhone[i];
      }
      return formattedNumber.startsWith('+') ? formattedNumber : '+' + formattedNumber;
    }

    return phoneValue;
  });

  // Priority map for common European countries - defined before it's used
  countryPriorityMap: { [iso2: string]: number } = {
    fr: 0, // France en premier

    // pays très courants (proches géographiquement ou liens forts)
    be: 1, // Belgique
    ch: 1, // Suisse
    lu: 1, // Luxembourg
    de: 1, // Allemagne
    it: 1, // Italie
    es: 1, // Espagne
    gb: 1, // Royaume-Uni

    // pays hors Europe très présents (business, diaspora)
    us: 2, // États-Unis
    ca: 2, // Canada
    ma: 2, // Maroc
    dz: 2, // Algérie
    tn: 2, // Tunisie

    // autres pays anglophones secondaires
    au: 3, // Australie
    nz: 3, // Nouvelle-Zélande
  };

  // Get countries
  countries: ICountry[] = getAllCountries();

  allCountries: ICountry[] = this.countries;

  /** Short dropdown list (priority map). France first when default country is not FR. */
  get filteredCountries(): ICountry[] {
    const sorted = this.countries
      .filter(country => country.iso2 in this.countryPriorityMap)
      .sort((a, b) => {
        const priorityA = this.countryPriorityMap[a.iso2] || 999;
        const priorityB = this.countryPriorityMap[b.iso2] || 999;
        return priorityA - priorityB;
      });
    return this.moveFranceFirstWhenDefaultIsNotFrance(sorted);
  }

  // Signals
  listOpen = signal(false);
  showExtendedList = signal(false);
  searchTerm = signal('');

  // Computed countries for extended view
  get filteredAllCountries(): ICountry[] {
    const term = this.searchTerm().toLowerCase();
    const list = !term
      ? [...this.allCountries]
      : this.allCountries.filter(
          country => country.name.toLowerCase().includes(term) || country.dialCode.includes(term),
        );
    return this.moveFranceFirstWhenDefaultIsNotFrance(list);
  }

  /**
   * When the default region is not France, pin France at the top of the dropdown
   * so it stays easy to select (short list + full list).
   */
  private moveFranceFirstWhenDefaultIsNotFrance(countries: ICountry[]): ICountry[] {
    const defaultLower = (this.defaultRegionCode() || 'fr').toLowerCase();
    if (defaultLower === 'fr') {
      return countries;
    }
    const frIndex = countries.findIndex(c => c.iso2 === 'fr');
    if (frIndex <= 0) {
      return countries;
    }
    const frCountry = countries[frIndex];
    return [frCountry, ...countries.slice(0, frIndex), ...countries.slice(frIndex + 1)];
  }

  constructor() {
    effect(
      () => {
        // if the phone as the dial code followed by a zero, remove the zero
        const phoneValue = this.phone();
        const dialCode = this.selectedCountryDialCode();
        if (phoneValue && dialCode && phoneValue.startsWith(`+${dialCode}0`)) {
          this.phone.set(phoneValue.replace(`+${dialCode}0`, `+${dialCode}`));
        }
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  ngOnInit(): void {
    const phone = this.phone();
    if (phone) {
      const matchedCountry = this.getCountryFromPhoneNumber(phone);
      if (matchedCountry) {
        this.selectedIso.set(matchedCountry.iso2);
      }
    }

    const selectedIso = this.selectedIso();
    if (!phone && selectedIso) {
      const countriesIndexed = keyBy(this.countries, 'iso2');
      const dialCode = countriesIndexed[selectedIso]?.dialCode;
      if (dialCode) {
        this.phone.set(`+${dialCode}`);
      }
    }

    // Default to France if no country is selected
    if (!selectedIso) {
      this.selectCountry(this.defaultRegionCode().toLowerCase() || 'fr');
    }
    this.updatePhone.emit(this.phone());
  }

  toggleCountriesList(event?: Event): void {
    // Stop event propagation to prevent the document click handler from firing
    if (event) {
      event.stopPropagation();
    }

    // Toggle the Signal value
    this.listOpen.update(value => !value);
  }

  selectCountry(iso: string | undefined): void {
    if (!iso) {
      return;
    }

    const countriesIndexed = keyBy(this.countries, 'iso2');
    this.selectedIso.set(iso);
    const dialCode = countriesIndexed[iso]?.dialCode;
    if (dialCode) {
      this.phone.set(`+${dialCode}`);
    }

    // Close both the dropdown and the extended list view
    this.listOpen.set(false);
    this.showExtendedList.set(false);
    this.updatePhone.emit(this.phone());
  }

  // Modified onChangePhone to handle local format input for France and Switzerland
  onChangePhone(phone: string): void {
    let processedPhone = phone;

    // When using local format, always treat input as local number (not international)
    // Only convert if the input doesn't already start with '+'
    if (this.useLocalFormat() && !phone.startsWith('+')) {
      const dialCode = this.selectedCountryDialCode();

      // French local format
      if (this.selectedIso() === 'fr') {
        // Remove leading 0 if present, then add +33 prefix
        const localNumber = phone.startsWith('0') ? phone.substring(1) : phone;
        processedPhone = '+33' + localNumber;
      }
      // Swiss local format
      else if (this.selectedIso() === 'ch') {
        // Remove leading 0 if present, then add +41 prefix
        const localNumber = phone.startsWith('0') ? phone.substring(1) : phone;
        processedPhone = '+41' + localNumber;
      }
      // For other countries using local format
      else if (dialCode) {
        // Remove leading 0 if present, then add dial code prefix
        const localNumber = phone.startsWith('0') ? phone.substring(1) : phone;
        processedPhone = '+' + dialCode + localNumber;
      }
    }

    const matchedCountry = this.getCountryFromPhoneNumber(processedPhone);
    if (matchedCountry) {
      this.selectedIso.set(matchedCountry.iso2);
    }

    const phoneNumberWithoutSpaces = processedPhone.replace(/[^0-9+]/g, '');
    this.phone.set(phoneNumberWithoutSpaces);
    this.updatePhone.emit(phoneNumberWithoutSpaces);
  }

  showAllCountries(event?: Event): void {
    // Prevent event propagation if event is provided
    if (event) {
      event.stopPropagation();
    }

    // Instead of closing the list, switch to extended view
    this.showExtendedList.set(true);
  }

  backToShortList(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showExtendedList.set(false);
  }

  private getCountryFromPhoneNumber(phoneNumber: string): ICountry | null {
    // Nettoyage du numéro (suppression des espaces et des tirets éventuels)
    phoneNumber = phoneNumber.replace(/[^0-9+]/g, '');

    // On retire le "+" pour ne garder que les chiffres
    let numberWithoutPlus = phoneNumber.replace(/^\+/, '');

    const allCountriesWithComparableDialCode = this.countries
      .map(country => {
        if (country.areaCodes) {
          return country.areaCodes.map(areaCode => {
            return {
              comparableDialCode: country.dialCode + areaCode,
              ...country,
            };
          });
        }
        return {
          comparableDialCode: country.dialCode,
          ...country,
        };
      })
      .flat();
    const fullMatchDialCodeCountry = this.recursiveFilteringCountriesByDialCodeAreaFullMatch(
      allCountriesWithComparableDialCode,
      numberWithoutPlus,
    );
    if (fullMatchDialCodeCountry) {
      return fullMatchDialCodeCountry;
    }
    return this.recursiveFilteringCountriesByDialCodePriority(this.countries, numberWithoutPlus);
  }

  private recursiveFilteringCountriesByDialCodeAreaFullMatch(
    allCountriesWithComparableDialCode: {
      name: string;
      iso2: string;
      dialCode: string;
      priority: number;
      areaCodes: string[] | null;
      comparableDialCode: string;
    }[],
    phoneNumber: string,
    index: number = 0,
  ):
    | (ICountry & {
        comparableDialCode: string;
      })
    | null {
    if (index >= phoneNumber.length) {
      return null;
    }
    if (phoneNumber.length < 4) {
      return null;
    }
    const dialCode = phoneNumber.substring(0, index + 1);
    const filteredCountries = allCountriesWithComparableDialCode.filter(country => {
      return country.comparableDialCode.startsWith(dialCode);
    });
    if (filteredCountries.length === 1) {
      const matchedCountry = filteredCountries[0];
      const isFullDialCodeMatch = dialCode === matchedCountry.comparableDialCode;
      if (!isFullDialCodeMatch) {
        return null;
      }
      return filteredCountries[0];
    }
    if (filteredCountries.length === 0) {
      return null;
    }
    return this.recursiveFilteringCountriesByDialCodeAreaFullMatch(
      allCountriesWithComparableDialCode,
      phoneNumber,
      index + 1,
    );
  }

  private recursiveFilteringCountriesByDialCodePriority(
    allCountriesWithComparableDialCode: ICountry[],
    phoneNumber: string,
    index: number = 0,
  ): ICountry | null {
    if (index >= phoneNumber.length) {
      return null;
    }
    if (phoneNumber.length < 4) {
      return null;
    }
    const dialCode = phoneNumber.substring(0, index + 1);
    const filteredCountries = allCountriesWithComparableDialCode.filter(country => {
      return country.dialCode.startsWith(dialCode);
    });
    if (filteredCountries.length === 1) {
      return filteredCountries[0];
    }
    if (filteredCountries.length === 0) {
      return null;
    }
    const result = this.recursiveFilteringCountriesByDialCodePriority(
      filteredCountries,
      phoneNumber,
      index + 1,
    );
    if (result) {
      return result;
    }
    const matchedCountryWithHighestPriority = filteredCountries.reduce((acc, country) => {
      return acc.priority < country.priority ? acc : country;
    });
    return matchedCountryWithHighestPriority;
  }

  selectedCountryDialCode = computed(() => {
    const selectedIsoValue = this.selectedIso();
    if (selectedIsoValue) {
      const countriesIndexed = keyBy(this.countries, 'iso2');
      return countriesIndexed[selectedIsoValue]?.dialCode || '';
    }
    return '';
  });

  /** Complete sample number for the selected country, e.g. +32 470 12 34 56 for Belgium. */
  readonly examplePhoneNumber = computed(() =>
    getExampleInternationalPhoneNumber(this.selectedIso(), this.selectedCountryDialCode()),
  );

  // Add this helper method to get the current country's dial code
  getSelectedCountryDialCode = (_selectedIso: string): string => {
    const selectedIsoValue = this.selectedIso();
    if (selectedIsoValue) {
      const countriesIndexed = keyBy(this.countries, 'iso2');
      return countriesIndexed[selectedIsoValue]?.dialCode || '';
    }
    return '';
  };

  // Add this function to help with rendering
  trackByIso(_index: number, country: ICountry): string {
    return country.iso2;
  }

  getFlagIcon = (iso2: string): string => {
    try {
      return `assets/flags/1x1/${iso2.toLowerCase()}.svg`;
    } catch {
      return 'assets/flags/1x1/xx.svg'; // fallback générique
    }
  };
}
